import { useEffect, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAuth } from "@/contexts/AuthContext";
import { frenchAuthError, isEmailNotConfirmed, estLimiteUneMinute, secondesAvantNouvelEnvoi } from "@/lib/auth-errors";
import { useDevBypassRedirect } from "@/hooks/use-dev-bypass-redirect";
import { NanaeLogoSymbol } from "@/components/qv3/NanaeLogo";
import { NanaeOAuthButtons } from "@/components/qorum/NanaeOAuthButtons";
import { socialMeta } from "@/lib/seo-meta";

// ── [LIEN DE CONNEXION — 03/08/2026] PLUS AUCUN MOT DE PASSE À SAISIR ────────────────────────────
//
// Le mot de passe n'est pas supprimé : aucun compte n'est invalidé, et qui veut le sien le garde.
// Mais il passe DERRIÈRE. L'ordre d'un écran est une recommandation qu'on ne prononce pas — ce qui
// est en haut est ce qu'on attend de vous, et ce qu'on attend désormais, c'est une adresse.
//
// Le mot de passe est le seul secret que l'utilisateur doit fabriquer, retenir et retaper. Le
// supprimer du chemin normal supprime la seule chose qui pouvait le bloquer dehors.
const schema = z.object({
  email: z.string().email("Adresse email invalide"),
  password: z.string().optional(),
});
type Values = z.infer<typeof schema>;

export const Route = createFileRoute("/login")({
  head: () => ({
    meta: [
      { title: "Accès — Hōjin" },
      { name: "description", content: "Connectez-vous à votre espace Hōjin pour retrouver vos saisines et verdicts." },
      { name: "robots", content: "noindex, follow" },
      ...socialMeta({
        title: "Accès — Hōjin",
        description: "Connectez-vous à votre espace Hōjin pour retrouver vos saisines et verdicts.",
        url: "https://hojin.ai/login",
        image: "conseil",
        card: "summary_large_image",
      }),
    ],
    links: [{ rel: "canonical", href: "https://hojin.ai/login" }],
  }),
  component: LoginPage,
});

const REMEMBER_KEY = "qorum.auth.remember";

export function LoginPage() {
  useDevBypassRedirect();
  const { signIn, resendConfirmation, envoyerLienDeConnexion } = useAuth();
  const navigate = useNavigate();
  const [formError, setFormError] = useState<string | null>(null);
  const [rememberMe, setRememberMe] = useState(true);
  // CTA de renvoi de l'email de confirmation, montré quand le login échoue sur
  // un email non confirmé.
  const [showResend, setShowResend] = useState(false);
  const [resendState, setResendState] = useState<"idle" | "sending" | "sent">("idle");
  /** Le mot de passe est REPLIÉ par défaut : il existe, il ne se propose pas. */
  const [motDePasseOuvert, setMotDePasseOuvert] = useState(false);
  const [lienEtat, setLienEtat] = useState<"repos" | "envoi" | "parti">("repos");
  /**
   * ⚠️ LE COMPTEUR DES 60 SECONDES — il rend le cas IMPOSSIBLE plutôt que rattrapé.
   *
   * Supabase refuse deux emails à la même adresse en moins d'une minute. Sans ce compteur, un
   * second clic partait, se faisait refuser, et l'écran disait pourtant « le lien vient de partir » :
   * l'utilisateur attendait un email qui n'existait pas. UN ÉTAT MUET EST UN MENSONGE D'ÉCRAN — et
   * celui-ci frappait exactement la personne qui bute déjà, celle qui reclique parce qu'elle doute.
   */
  const [attente, setAttente] = useState(0);
  const [messageAttente, setMessageAttente] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<Values>({ resolver: zodResolver(schema) });

  // Échec d'une connexion Google/Apple : le callback renvoie ?error=oauth_failed.
  // On l'affiche pour ne pas laisser l'utilisateur revenir sans explication.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const err = params.get("error");
    if (err === "oauth_failed") {
      setFormError("La connexion avec Google / Apple a échoué. Réessayez.");
    } else if (err === "lien_expire") {
      // ⚠️ JAMAIS UNE ERREUR NUE. Un lien de connexion périmé n'est pas une faute de
      // l'utilisateur : c'est le fonctionnement normal d'un lien à usage unique. L'écran le dit
      // et remet le geste à portée — le champ email est juste au-dessus.
      setFormError("Ce lien de connexion a expiré ou a déjà servi. Saisissez votre email ci-dessous, nous vous en envoyons un nouveau.");
    }
  }, []);

  // Le décompte, seconde par seconde. Il libère le bouton de lui-même : rien à recliquer pour
  // « débloquer », et aucune promesse d'un envoi qui ne partirait pas.
  useEffect(() => {
    if (attente <= 0) return;
    const t = window.setTimeout(() => setAttente((n) => n - 1), 1000);
    return () => window.clearTimeout(t);
  }, [attente]);

  const onResend = async () => {
    setResendState("sending");
    try {
      await resendConfirmation(getValues("email"));
      setResendState("sent");
    } catch {
      setResendState("idle");
      setFormError("Impossible de renvoyer l'email. Réessayez dans quelques minutes.");
    }
  };

  const onEnvoyerLien = handleSubmit(async (values) => {
    setFormError(null);
    setShowResend(false);
    setLienEtat("envoi");
    setMessageAttente(null);
    try {
      await envoyerLienDeConnexion(values.email);
      setLienEtat("parti");
      setAttente(60);
    } catch (e) {
      setLienEtat("repos");
      // La limite d'une minute n'est PAS une erreur : c'est une seconde d'attente. La rendre en
      // rouge ferait croire à un échec là où le premier lien est bel et bien parti.
      if (estLimiteUneMinute(e)) {
        const s = secondesAvantNouvelEnvoi(e);
        setAttente(s);
        setMessageAttente("Un lien vient déjà d'être envoyé à cette adresse. Patientez une minute avant d'en redemander un.");
      } else {
        setFormError(frenchAuthError(e));
      }
    }
  });

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    setShowResend(false);
    setResendState("idle");
    try {
      await signIn(values.email, values.password ?? "");
      try {
        if (rememberMe) {
          localStorage.setItem(REMEMBER_KEY, "1");
        } else {
          localStorage.removeItem(REMEMBER_KEY);
        }
      } catch {
        /* storage indisponible : on ignore */
      }
      navigate({ to: "/foyer" });
    } catch (e) {
      setFormError(frenchAuthError(e));
      setShowResend(isEmailNotConfirmed(e));
    }
  });

  return (
    <div className="nanae-auth-shell qv3 qv3-dark">
      <main className="nanae-auth-main">
        <section className="nanae-auth-card" aria-labelledby="login-title">
          <span className="nanae-auth-card-corner left">N°ACCES · MMXXVI</span>
          {formError && (
            <span className="nanae-auth-card-corner right">
              <span className="qv3-led" aria-hidden="true" />
              ERR
            </span>
          )}

          <div className="nanae-auth-logo">
            <NanaeLogoSymbol size="xl" />
          </div>

          <p className="nanae-auth-eyebrow">§ ACCÈS · CONSEIL DES SEPT</p>
          <h1 id="login-title" className="nanae-auth-title">
            Le Conseil vous attend.
          </h1>
          <p className="nanae-auth-subtitle">Sept intelligences. Une clarté.</p>

          <NanaeOAuthButtons label="signin" />

          {/* ── LE LIEN DE CONNEXION, EN PREMIER ─────────────────────────────────────────────
              Un champ, un bouton. C'est le chemin normal ; le mot de passe est en repli dessous. */}
          <form onSubmit={onEnvoyerLien} noValidate style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            <div className="qv3-field">
              <label className="qv3-field-label" htmlFor="login-email">E-MAIL</label>
              <input
                id="login-email"
                type="email"
                autoComplete="email"
                className="qv3-input-lcd"
                aria-invalid={errors.email ? "true" : "false"}
                placeholder="vous@domaine.com"
                {...register("email")}
              />
              {errors.email && <p className="qv3-field-error">— {errors.email.message}</p>}
            </div>

            {/* ⚠️ LA CONFIRMATION NE REMPLACE PLUS LE BOUTON — ELLE S'AJOUTE.
                Défaut trouvé en sondant la prod : une fois le lien parti, la confirmation prenait la
                place du bouton et ne la rendait jamais. L'écran écrivait « vous pourrez en redemander
                un dans 56 secondes »… et n'offrait AUCUN moyen de le faire. Il fallait recharger la
                page pour en obtenir un second — ce que personne ne devine.
                Une phrase qui promet un geste que l'écran n'offre pas est un cul-de-sac poli. */}
            {lienEtat === "parti" && (
              // ⚠️ MÊME PHRASE POUR UN COMPTE QUI EXISTE ET POUR UN COMPTE QUI N'EXISTE PAS.
              // Distinguer les deux offrirait à n'importe qui un oracle : « cette adresse a-t-elle
              // un compte chez vous ? ». On préfère l'ambiguïté pour celui qui se trompe d'adresse
              // à la divulgation sur le compte d'un tiers.
              <p role="status" className="nanae-auth-error" style={{ opacity: 0.9 }}>
                Si un compte existe pour cette adresse, le lien de connexion vient de partir.
                Ouvrez votre boîte de réception — <strong>pensez à vérifier vos indésirables</strong>.
                {attente > 0
                  ? ` Vous pourrez en redemander un dans ${attente} seconde${attente > 1 ? "s" : ""}.`
                  : " Vous pouvez en redemander un ci-dessous si besoin."}
              </p>
            )}

            {/* Le bouton reste TOUJOURS servi : c'est la seule sortie de cet écran. Désactivé
                pendant le décompte, il se libère seul et redevient cliquable — sans rechargement. */}
            <button
              type="submit"
              className="qv3-btn-hifi"
              disabled={isSubmitting || lienEtat === "envoi" || attente > 0}
            >
              {lienEtat === "envoi"
                ? "Envoi du lien…"
                : attente > 0
                  ? `Nouveau lien possible dans ${attente} s`
                  : lienEtat === "parti"
                    ? "Renvoyer un lien de connexion"
                    : "Recevoir mon lien de connexion"}
              {lienEtat !== "envoi" && attente === 0 && <span className="qv3-btn-hifi-hint">ENTER →</span>}
            </button>

            {/* ── LE MOT DE PASSE, EN REPLI ────────────────────────────────────────────────────
                Il n'est ni supprimé ni invalidé : aucun compte ne perd le sien. Il est simplement
                sorti du chemin par défaut, et ne s'ouvre que si on le demande. */}
            {!motDePasseOuvert ? (
              <button
                type="button"
                onClick={() => setMotDePasseOuvert(true)}
                style={{
                  background: "none", border: "none", padding: 0, cursor: "pointer",
                  fontFamily: '"DM Mono", monospace', fontSize: 11, letterSpacing: "1.5px",
                  textTransform: "uppercase", color: "var(--charcoal, #1F1F22)", opacity: 0.65,
                  textDecoration: "underline", textUnderlineOffset: 4, alignSelf: "flex-start",
                }}
              >
                ou avec votre mot de passe
              </button>
            ) : (
              <div className="qv3-field">
                <label className="qv3-field-label" htmlFor="login-password">MOT DE PASSE</label>
                <input
                  id="login-password"
                  type="password"
                  autoComplete="current-password"
                  className="qv3-input-lcd"
                  aria-invalid={errors.password ? "true" : "false"}
                  placeholder="••••••••"
                  {...register("password")}
                />
                {errors.password && <p className="qv3-field-error">— {errors.password.message}</p>}
                <button
                  type="button"
                  onClick={() => void onSubmit()}
                  disabled={isSubmitting}
                  className="qv3-btn-hifi"
                  style={{ marginTop: 14 }}
                >
                  Entrer avec mon mot de passe
                </button>
              </div>
            )}

            <label
              htmlFor="login-remember"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                fontFamily: '"DM Mono", monospace',
                fontSize: 11,
                letterSpacing: "1.5px",
                textTransform: "uppercase",
                color: "var(--charcoal, #1F1F22)",
                cursor: "pointer",
                userSelect: "none",
              }}
            >
              <input
                id="login-remember"
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                style={{
                  width: 14,
                  height: 14,
                  accentColor: "var(--accent-cramoisi, #000000)",
                  cursor: "pointer",
                }}
              />
              Rester connecté sur ce navigateur
            </label>

            {messageAttente && (
              <p role="status" className="nanae-auth-error" style={{ opacity: 0.85 }}>
                {messageAttente}
              </p>
            )}

            {formError && (
              <p role="alert" className="nanae-auth-error">
                <span className="qv3-led" aria-hidden="true" />
                {formError}
              </p>
            )}

            {showResend && (
              resendState === "sent" ? (
                <p role="status" className="nanae-auth-error" style={{ opacity: 0.85 }}>
                  Email de confirmation renvoyé. Vérifiez votre boîte de réception.
                </p>
              ) : (
                <button
                  type="button"
                  onClick={onResend}
                  disabled={resendState === "sending"}
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    textAlign: "left",
                    color: "var(--accent-cramoisi, #000000)",
                    textDecoration: "underline",
                    cursor: resendState === "sending" ? "default" : "pointer",
                    fontFamily: "inherit",
                    fontSize: 13,
                  }}
                >
                  {resendState === "sending" ? "Envoi…" : "Renvoyer l'email de confirmation"}
                </button>
              )
            )}

          </form>

          <div className="nanae-auth-altlinks">
            <Link to="/signup" className="nanae-auth-altlink">
              Première saisine ? <strong>S'inscrire</strong>
            </Link>
            <Link to="/forgot-password" className="nanae-auth-altlink">
              Mot de passe oublié
            </Link>
          </div>
        </section>
      </main>
      <footer className="nanae-auth-foot" data-rp="auth-footer">SEPT VOIX, UNE DIRECTION · MMXXVI</footer>
    </div>
  );
}
